"use client";

import { useState, useEffect, useMemo, useCallback } from 'react';

interface UseTypingEffectProps {
  texts: string[];
  typingSpeed?: number;
  fadeSpeed?: number;
  pauseTime?: number;
}

export const useTypingEffect = ({
  texts,
  typingSpeed = 20,
  fadeSpeed = 100,
  pauseTime = 500
}: UseTypingEffectProps) => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isFading, setIsFading] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [opacity, setOpacity] = useState(1);

  // Memoize hàm để tách text thành các phần tử (text và HTML tags)
  const parseTextWithHTML = useCallback((text: string) => {
    const regex = /(<[^>]+>)/g;
    const parts = text.split(regex);

    return parts.filter(part => part.length > 0);
  }, []);

  // Memoize hàm để lấy text hiển thị từ các phần tử đã parse
  const getDisplayText = useCallback((parts: string[], currentLength: number) => {
    let result = '';
    let charCount = 0;

    for (const part of parts) {
      if (part.startsWith('<') && part.endsWith('>')) {
        // Đây là HTML tag, thêm vào ngay lập tức
        result += part;
      } else {
        // Đây là text, thêm từng ký tự
        const remainingChars = currentLength - charCount;

        if (remainingChars > 0) {
          result += part.slice(0, remainingChars);
          charCount += part.length;
        }
        if (charCount >= currentLength) break;
      }
    }

    return result;
  }, []);

  // Memoize parsed text để tránh parse lại không cần thiết
  const currentTextParts = useMemo(() => {
    if (texts.length === 0 || !texts[currentTextIndex]) return [];

    return parseTextWithHTML(texts[currentTextIndex]);
  }, [texts, currentTextIndex, parseTextWithHTML]);

  useEffect(() => {
    if (texts.length === 0) return;

    const textParts = currentTextParts;

    if (isPaused) {
      const pauseTimer = setTimeout(() => {
        setIsPaused(false);
        setIsFading(true);
        setOpacity(0);
      }, pauseTime);

      return () => clearTimeout(pauseTimer);
    }

    if (isFading) {
      if (opacity === 0) {
        setIsFading(false);
        setCurrentTextIndex((prev) => (prev + 1) % texts.length);
        setCurrentText('');
        setOpacity(1);

        return;
      }

      const fadeTimer = setTimeout(() => {
        setOpacity(0);
      }, fadeSpeed);

      return () => clearTimeout(fadeTimer);
    }

    // Tính tổng số ký tự text (không tính HTML tags)
    const totalTextChars = textParts
      .filter(part => !part.startsWith('<') || !part.endsWith('>'))
      .join('').length;

    // Tính số ký tự text hiện tại
    const currentTextChars = currentText
      .replace(/<[^>]+>/g, '')
      .length;

    if (currentTextChars >= totalTextChars) {
      setIsPaused(true);

      return;
    }

    const typeTimer = setTimeout(() => {
      const newLength = currentTextChars + 1;
      const newText = getDisplayText(textParts, newLength);

      setCurrentText(newText);
    }, typingSpeed);

    return () => clearTimeout(typeTimer);
  }, [currentText, currentTextIndex, isFading, isPaused, opacity, texts, typingSpeed, fadeSpeed, pauseTime, currentTextParts, getDisplayText]);

  return { currentText, opacity };
};
